import { useState, Fragment, useEffect } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Card from '@mui/material/Card';
import CloseIcon from '@mui/icons-material/Close';

// self defined
import api from 'utils/api';
import { Box, Typography } from '@mui/material';
import { isCurrentUserTaskManager } from 'utils/privilege';
import { Actions, StyledButton } from 'shared/modals/Styles';
import localData from 'utils/localData';
import { USER_COMMENT } from 'shared/constants/comment';
import { KENSHIN_API } from 'shared/constants/apis';
import { getSelectedIssuesListTitle, getSelectedIssuesList } from './utils';
import ToggleStatus from '../Status';
import {
  mixin,
  modalBorderRadius,
  modalWidths,
  zIndexValues,
} from 'utils/styles';
import { HorizontalDivider } from './styles';
import { USER_DATA } from 'shared/constants/users';

const propTypes = {
  isOpened: PropTypes.bool,
  closeModal: PropTypes.func,
  issues: PropTypes.array.isRequired,
  selectedIssueIndexes: PropTypes.array.isRequired,
  doctors: PropTypes.array.isRequired,
  updateLocalProjectIssues: PropTypes.func,
  handleOpenIssueDetails: PropTypes.func,
};

const BatchEditModal = ({
  isOpened,
  closeModal,
  issues,
  selectedIssueIndexes,
  updateLocalProjectIssues,
  handleOpenIssueDetails,
}) => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalUpdateData, setConfirmModalUpdateData] = useState();
  const [, setConfirmModalInfo] = useState({
    source: '',
    destination: '',
  });
  const [selectedIssues, setSelectedIssues] = useState([
    ...selectedIssueIndexes.map((index) => issues[index]),
  ]);

  useEffect(() => {
    const tmp = selectedIssueIndexes.map((index) => issues[index]);
    setSelectedIssues([...tmp]);
  }, [issues, selectedIssueIndexes]);

  const batchUpdateIssues = (updatedFields, issues) => {
    api.optimisticBatchUpdateIssues(
      `${KENSHIN_API.ISSUES_BATCH_UPDATE}`,
      {
        issues,
        updatedFields,
        setLocalData: (issueId, fields) => {
          // this fields is either `updatedFields` or `currentFields` above;
          // this is the local action that is being executed
          updateLocalProjectIssues(issueId, fields);
        },
      },
      {
        params: {
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
          username: localData.get(USER_DATA.USERNAME),
          userId: localData.get(USER_COMMENT.USER_ID),
        },
        baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      }
    );
  };

  const isTaskManager = isCurrentUserTaskManager();

  const handleConfirmModalYesButtonClick = async () => {
    await batchUpdateIssues(confirmModalUpdateData, selectedIssues);
    setConfirmModalOpen(false);
  };

  const handleConfirmModalNoButtonClick = () => {
    setConfirmModalOpen(false);
  };

  return (
    <Fragment>
      <Modal
        disableEscapeKeyDown
        open={isOpened}
        onClose={closeModal}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '50vw',
          zIndex: zIndexValues.batchEditModal,
        }}>
        <Card
          sx={{
            minHeight: '30%',
            maxHeight: '75%',
            width: '60%',
            borderRadius: modalBorderRadius.sm,
          }}>
          <Grid container justify="flex-end">
            <Grid size={12} item display="flex" justifyContent="flex-end">
              <IconButton
                aria-label="delete"
                size="small"
                variant="empty"
                onClick={closeModal}
                sx={{ paddingRight: 1, paddingTop: 1 }}>
                <CloseIcon fontSize="medium" />
              </IconButton>
            </Grid>
          </Grid>

          <Box mt={1} mb={2} ml={4}>
            <Typography variant="h5" component="h2">
              {intl.get(
                'kenshin_received_history_case_batch_edit_modal_text_edit_all'
              )}
            </Typography>
          </Box>

          <Grid container spacing={2} justifyContent="center">
            <Grid>
              {isTaskManager === true && (
                <ToggleStatus
                  issues={selectedIssues}
                  setConfirmModalInfo={setConfirmModalInfo}
                  setConfirmModalUpdateData={setConfirmModalUpdateData}
                  setConfirmModalOpen={setConfirmModalOpen}
                />
              )}
            </Grid>
          </Grid>

          <Box my={1} ml={4}>
            <Typography variant="h5" component="h2">
              {intl
                .get(
                  'kenshin_received_history_case_batch_edit_modal_text_already_selected'
                )
                .replace('{arg1}', `${selectedIssues.length}`)}
            </Typography>
          </Box>

          {getSelectedIssuesListTitle()}

          {/* Horizontal Divider */}
          <HorizontalDivider />

          <Card
            sx={{
              boxShadow: 'none',
              margin: '2px 2px 40px 2px',
              overflowY: 'scroll',
              ...mixin.simpleCustomScrollbar,
            }}>
            {getSelectedIssuesList(selectedIssues, handleOpenIssueDetails)}
          </Card>
        </Card>
      </Modal>

      {confirmModalOpen && (
        <Modal
          disableEscapeKeyDown
          open={confirmModalOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '50vw',
          }}>
          <Card
            sx={{
              maxHeight: '50%',
              width: modalWidths.xs,
              borderRadius: modalBorderRadius.sm,
              padding: '30px',
            }}>
            <Box>
              <Grid container justify="center" mb={5}>
                <Grid size={12} item display="flex" justifyContent="flex-start">
                  <Typography variant="h5">
                    {intl
                      .get(
                        'kenshin_received_history_case_batch_edit_modal_text_hint_next'
                      )
                      .replace('{arg1}', `${selectedIssues.length}`)}
                  </Typography>
                </Grid>
              </Grid>
              <Actions>
                <StyledButton
                  variant="primary"
                  onClick={handleConfirmModalYesButtonClick}>
                  {intl.get('kanban_confirm_popup_window_button_yes')}
                </StyledButton>
                <StyledButton hollow onClick={handleConfirmModalNoButtonClick}>
                  {intl.get('kanban_confirm_popup_window_button_no')}
                </StyledButton>
              </Actions>
            </Box>
          </Card>
        </Modal>
      )}
    </Fragment>
  );
};

BatchEditModal.propTypes = propTypes;

export default BatchEditModal;
